import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, Info, IndianRupee, Zap, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

/**
 * Setup panel: host locks in day-one base rate for every unit on the property.
 * Anything left blank falls back to the city ADR default until saved.
 */
export default function BaseRatesPanel({ pid, propertyName, connectedChannels = 0, onSaved }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({}); // { unit_id: string }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setData(null); setEdits({});
    api.get(`/pms/properties/${pid}/base-rates`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(errMsg(e)));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const rateFor = (u) => edits[u.unit_id] !== undefined ? edits[u.unit_id] : (u.base_rate ?? "");
  const setRate = (uid, val) => setEdits((e) => ({ ...e, [uid]: val.replace(/[^\d.]/g, "") }));
  const applyDefaultToAll = () => {
    if (!data) return;
    const next = {};
    for (const u of data.units) if (u.base_rate == null) next[u.unit_id] = String(data.city_default);
    setEdits((e) => ({ ...e, ...next }));
    toast.success(`Filled ${Object.keys(next).length} unit${Object.keys(next).length === 1 ? "" : "s"} with the ${data.city} default of ₹${fmt(data.city_default)}`);
  };

  const save = async () => {
    if (!data) return;
    const rates = data.units.map((u) => {
      const raw = rateFor(u);
      const num = parseFloat(String(raw));
      return { unit_id: u.unit_id, base_rate: isNaN(num) ? null : num };
    }).filter((r) => r.base_rate !== null && r.base_rate >= 0);
    if (!rates.length) return toast.error("Enter at least one base rate before saving.");
    setBusy(true);
    try {
      const { data: r } = await api.put(`/pms/properties/${pid}/base-rates`, { rates });
      toast.success(`${r.saved} unit base rate${r.saved === 1 ? "" : "s"} locked · broadcast to ${r.channels_broadcast} channel${r.channels_broadcast === 1 ? "" : "s"}`);
      onSaved?.();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-300" size={28} /></div>;

  const missing = data.units.filter((u) => !u.is_set).length;
  const dirty = Object.keys(edits).length > 0;

  return (
    <div className="reveal space-y-5">
      <div className={`rounded-2xl border p-5 ${data.all_set ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        <div className="flex items-start gap-3">
          {data.all_set ? <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={18} /> : <Info className="text-amber-600 shrink-0 mt-0.5" size={18} />}
          <div className="flex-1">
            <h3 className="font-display font-bold text-slate-900 text-sm">
              {data.all_set ? "Every unit has a locked base rate." : `${missing} unit${missing === 1 ? "" : "s"} still need${missing === 1 ? "s" : ""} a base rate.`}
            </h3>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              The base rate is your day-one price for each unit. Every direct booking, OTA channel and PMS calendar reads from this value first — then any per-day override you set later takes precedence. City default for <b>{data.city || "this location"}</b>: ₹{fmt(data.city_default)}.
            </p>
          </div>
          {!data.all_set && (
            <button data-testid="br-apply-default" onClick={applyDefaultToAll} className="text-[11px] px-3 py-1.5 rounded-full bg-white border border-amber-200 hover:border-amber-400 text-amber-700 font-semibold whitespace-nowrap">
              Fill blanks with ₹{fmt(data.city_default)}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-slate-900 text-sm">Base rate per unit</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">{propertyName} · {data.units.length} unit{data.units.length === 1 ? "" : "s"}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 flex items-center gap-1.5"><Zap size={11} className="brand-pink" /> Pushes to {connectedChannels} channel{connectedChannels === 1 ? "" : "s"} on save</span>
            <button data-testid="br-save" onClick={save} disabled={!dirty || busy}
              className="btn-brand rounded-lg px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save base rates
            </button>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {data.units.map((u) => {
            const val = rateFor(u);
            const isEdited = edits[u.unit_id] !== undefined;
            return (
              <div key={u.unit_id} data-testid={`br-row-${u.unit_id}`} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-slate-800 text-sm">Unit {u.name}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {u.is_set ? <>Locked at ₹{fmt(u.base_rate)}</> : <>Suggestion: ₹{fmt(u.suggested_rate)}</>}
                  </p>
                </div>
                <div className="relative w-40">
                  <IndianRupee size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid={`br-input-${u.unit_id}`} type="text" inputMode="decimal"
                    value={val} onChange={(e) => setRate(u.unit_id, e.target.value)}
                    placeholder={String(u.suggested_rate)}
                    className={`w-full pl-8 pr-3 py-2 text-sm font-mono font-bold text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 ${isEdited ? "border-[#4F46E5] text-[#4F46E5]" : u.is_set ? "border-emerald-200 text-slate-800" : "border-slate-200 text-slate-500"}`} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-[11px] text-slate-400">Change one number, we push it to every connected channel — Atiti, Booking.com, Airbnb, MakeMyTrip and every OTA you add later. No manual re-entry.</p>
    </div>
  );
}
